import type { PropsWithChildren } from 'react'
import { useContext } from 'react'

import { ShopContext } from '~/contexts'
import { useShop } from '~/hooks/data/use-shop'
import { cn } from '~/utils/classnames'

import type { Mode } from './tokens'

type ThemeUnion = 'protocol-light' | 'protocol-dark' | 'shop'

type ThemeProps = PropsWithChildren<{
  theme: ThemeUnion
  className?: string
  mode?: Mode | ((mode: Mode | null) => Mode | null)
}>

export const Theme = ({ theme, mode, children, className }: ThemeProps) => {
  return theme === 'shop' ? (
    <ShopTheme className={className} mode={mode}>
      {children}
    </ShopTheme>
  ) : (
    <div
      className={cn('contents', className)}
      data-theme={theme}
      style={{ colorScheme: theme == 'protocol-dark' ? 'dark' : 'light' }}
    >
      {children}
    </div>
  )
}

const ShopTheme = ({
  mode,
  children,
  className,
}: Omit<ThemeProps, 'theme'>) => {
  const [contextShop] = useContext(ShopContext) // get mode from context to respond to customizer changes
  const shop = useShop()

  const shopMode = contextShop ? contextShop.themeMode : shop.themeMode

  // Forcing a specific mode (styling) to apply
  const propsMode = typeof mode === 'function' ? mode(shopMode) : mode
  const modeToApply = propsMode ?? shopMode ?? 'light'

  // shop data-theme needs to include the mode after an hyphen
  // this way we can target specific code to specific modes using tailwind's light: dark: and overall: prefixes

  return (
    <div
      className={cn('contents', className)}
      data-theme={`shop-${modeToApply}`}
    >
      {children}
    </div>
  )
}
